import React, { useState, useEffect } from 'react';
import { Routes, Route, useLocation, useNavigate } from 'react-router-dom';
import Header from './components/Header';
import Hero from './components/Hero';
import Services from './components/Services';
import About from './components/About';
import PriceCalculator from './components/PriceCalculator';
import Testimonials from './components/Testimonials';
import Footer from './components/Footer';
import ContactPage from './pages/ContactPage';
import ServiceDetailPage from './pages/ServiceDetailPage';
import ScrollReveal from './components/ScrollReveal';

function App() {
  const [services, setServices] = useState([]);
  const [testimonials, setTestimonials] = useState([]);
  const [prefilledMessage, setPrefilledMessage] = useState('');
  const [selectedService, setSelectedService] = useState('');
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const fetchData = async () => {
      try {
        // Fetch from public CMS endpoints in parallel
        const [servicesRes, testimonialsRes] = await Promise.all([
          fetch('https://api.dev.auxo.dk/public/content/services'),
          fetch('https://api.dev.auxo.dk/public/content/testimonials')
        ]);

        if (servicesRes.ok) {
          const servicesData = await servicesRes.json();
          setServices(servicesData);
        }
        if (testimonialsRes.ok) {
          const testimonialsData = await testimonialsRes.json();
          setTestimonials(testimonialsData);
        }
      } catch (error) {
        console.error('Fejl ved hentning af CMS-data:', error);
      }
    };

    fetchData();
  }, []);

  // Intelligent hash scrolling when route changes
  useEffect(() => {
    if (location.hash) {
      const timer = setTimeout(() => {
        const element = document.querySelector(location.hash);
        if (element) {
          const offset = 80; // Header height
          const bodyRect = document.body.getBoundingClientRect().top;
          const elementRect = element.getBoundingClientRect().top;
          const elementPosition = elementRect - bodyRect;
          const offsetPosition = elementPosition - offset;

          window.scrollTo({
            top: offsetPosition,
            behavior: 'smooth'
          });
        }
      }, 150);
      return () => clearTimeout(timer);
    } else {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }, [location.pathname, location.hash]);

  // Scroll to contact form and pre-fill message
  const handleSendInquiry = (message) => {
    setPrefilledMessage(message);
    scrollToSection('#hero-form');
  };

  // Scroll to contact form and pre-fill with selected service
  const handleSelectService = (serviceTitle) => {
    navigate('/kontakt', { state: { service: serviceTitle } });
  };

  // Scroll to price calculator
  const handleSecondaryClick = () => {
    scrollToSection('#prisberegner');
  };

  // Helper to scroll smoothly with offset
  const scrollToSection = (selector) => {
    const element = document.querySelector(selector);
    if (element) {
      const offset = 80; // Header height
      const bodyRect = document.body.getBoundingClientRect().top;
      const elementRect = element.getBoundingClientRect().top;
      const elementPosition = elementRect - bodyRect;
      const offsetPosition = elementPosition - offset;

      window.scrollTo({
        top: offsetPosition,
        behavior: 'smooth'
      });
    }
  };

  return (
    <div className="min-h-screen bg-white text-slate-900 selection:bg-[#0e227b] selection:text-white">
      {/* Navigation Header */}
      <Header services={services} />

      {/* Main Content */}
      <main>
        <Routes>
          {/* Home Route */}
          <Route path="/" element={
            <>
              {/* Hero Section */}
              <Hero 
                services={services}
                prefilledMessage={prefilledMessage}
                selectedService={selectedService}
                onSecondaryClick={handleSecondaryClick} 
              />
            </>
          } />

          {/* Dedicated Contact Route */}
          <Route path="/kontakt" element={
            <ContactPage services={services} />
          } />

          {/* Service Detail Route */}
          <Route path="/ydelser/:slug" element={
            <ServiceDetailPage 
              services={services} 
              onSelectService={handleSelectService} 
            />
          } />
        </Routes>
      </main>

      {/* Footer */}
      <Footer />
    </div>
  );
}

export default App;
